import { useState, useEffect, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Search, Sparkles, Building2, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { useFavorites } from '@/arche/arches/favorites/hooks/useFavorites';
import { useArcheDisplayMap } from '../hooks/useArcheDisplayMap';
import api from '@/lib/api';

/** Sentinel picker values distinct from any arche id. */
export const ARCHE_AUTO = '__auto__';
export const ARCHE_SYSTEM = '__system__';

const AUTO_LABEL = 'Auto-detect (let support figure it out)';
const SYSTEM_LABEL = 'Fias system (not arche-specific)';

/**
 * Convert a picker selection into the create-submission arche fields.
 * - Auto-detect → archeId null, no source (the async pipeline infers it later).
 * - Fias system → archeId null, source 'user' (locks out AI; user said platform).
 * - A specific arche → that id. Source is 'route' when the value is exactly the
 *   one prefilled from the launch context (`?arche=`) and the user left it as-is,
 *   else 'user' (an explicit choice). Both lock out AI inference.
 *
 * @param routePrefillArcheId the arche id that was prefilled from the URL (the
 *   raw `?arche=` value), or null when the form wasn't launched from an arche.
 */
export function archePickerToTag(
  value: string,
  routePrefillArcheId?: string | null,
): {
  archeId: string | null;
  archeTagSource?: 'route' | 'user';
} {
  if (value === ARCHE_AUTO) return { archeId: null };
  if (value === ARCHE_SYSTEM) return { archeId: null, archeTagSource: 'user' };
  const source = routePrefillArcheId && value === routePrefillArcheId ? 'route' : 'user';
  return { archeId: value, archeTagSource: source };
}

interface ArcheResult {
  archId: string;
  name: string;
  imageRef: string | null;
}

interface ArchePickerProps {
  value: string;
  onChange: (value: string) => void;
  /** Optional label text; omit to render just the picker. */
  label?: string;
}

function ArcheIcon({ imageRef }: { imageRef: string | null }) {
  return imageRef ? (
    <ArcheIconImg imageRef={imageRef} size={64} alt="" className="h-4 w-4 shrink-0 rounded-sm" />
  ) : (
    <DefaultArcheIcon className="h-4 w-4 shrink-0 rounded-sm" />
  );
}

function PickerRow({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted',
        active && 'bg-muted',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Lets a user say which arche their report concerns. Defaults to "Auto-detect"
 * so an untouched submission is left for the AI to tag.
 *
 * The picker is a searchable combobox: before typing it offers the user's
 * worktop arches as quick picks, and typing runs a debounced TEXT search over
 * the whole published, listed catalog — so any arche is reachable, not just the
 * worktop. Semantic search is disabled (`enableSemanticSearch: false`) so the
 * type-ahead is free (no embedding credits).
 */
export function ArchePicker({ value, onChange, label = 'What is this about?' }: ArchePickerProps) {
  const { favorites } = useFavorites();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ArcheResult[]>([]);
  const [loading, setLoading] = useState(false);
  // Names/icons of arches chosen from search, so the trigger can label a pick
  // that isn't on the user's worktop without an extra round-trip.
  const [pickedById, setPickedById] = useState<Record<string, ArcheResult>>({});

  const searching = q.trim().length >= 2;

  // Debounced text-only search over published, listed arches. Runs only while
  // the popover is open and the term is long enough to be meaningful.
  useEffect(() => {
    if (!open || !searching) {
      setResults([]);
      setLoading(false);
      return;
    }
    const term = q.trim();
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .searchMarketplace({ q: term, enableSemanticSearch: false, limit: 20 })
        .then((r) =>
          setResults(
            r.arches.map((a) => ({ archId: a.archId, name: a.name, imageRef: a.imageRef })),
          ),
        )
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [q, open, searching]);

  const isArcheId = value !== ARCHE_AUTO && value !== ARCHE_SYSTEM;

  // Resolve the display name for an arche id that's neither on the worktop nor
  // freshly picked (e.g. one prefilled from `?arche=`). No id → no request.
  const fav = favorites.find((f) => f.archId === value);
  const needsResolve = isArcheId && !fav && !pickedById[value];
  const { metaById } = useArcheDisplayMap(needsResolve ? [value] : []);

  const selectedLabel = useMemo(() => {
    if (value === ARCHE_AUTO) return AUTO_LABEL;
    if (value === ARCHE_SYSTEM) return SYSTEM_LABEL;
    if (fav) return fav.archeName;
    if (pickedById[value]) return pickedById[value].name;
    return metaById.get(value)?.name ?? 'Selected arche';
  }, [value, fav, pickedById, metaById]);

  const selectedImageRef = isArcheId
    ? (pickedById[value]?.imageRef ?? metaById.get(value)?.imageRef ?? null)
    : null;

  const choose = (next: string, meta?: ArcheResult) => {
    if (meta) setPickedById((m) => ({ ...m, [meta.archId]: meta }));
    onChange(next);
    setOpen(false);
    setQ('');
    setResults([]);
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="flex min-w-0 items-center gap-2">
              {value === ARCHE_AUTO ? (
                <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : value === ARCHE_SYSTEM ? (
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ArcheIcon imageRef={selectedImageRef} />
              )}
              <span className="truncate">{selectedLabel}</span>
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search all arches…"
              className="h-8 pl-7 text-sm"
              maxLength={100}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {/* Always-available, non-arche choices. */}
            <PickerRow active={value === ARCHE_AUTO} onClick={() => choose(ARCHE_AUTO)}>
              <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" />
              {AUTO_LABEL}
            </PickerRow>
            <PickerRow active={value === ARCHE_SYSTEM} onClick={() => choose(ARCHE_SYSTEM)}>
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              {SYSTEM_LABEL}
            </PickerRow>
            <div className="my-1 border-t" />

            {searching ? (
              <>
                {loading && (
                  <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Searching…
                  </div>
                )}
                {!loading && results.length === 0 && (
                  <div className="px-2 py-2 text-xs text-muted-foreground">No arches found.</div>
                )}
                {!loading &&
                  results.map((a) => (
                    <PickerRow
                      key={a.archId}
                      active={value === a.archId}
                      onClick={() => choose(a.archId, a)}
                    >
                      <ArcheIcon imageRef={a.imageRef} />
                      <span className="truncate">{a.name}</span>
                    </PickerRow>
                  ))}
              </>
            ) : favorites.length > 0 ? (
              <>
                <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Your worktop
                </div>
                {favorites.map((f) => (
                  <PickerRow
                    key={f.archId}
                    active={value === f.archId}
                    onClick={() =>
                      choose(f.archId, { archId: f.archId, name: f.archeName, imageRef: null })
                    }
                  >
                    <DefaultArcheIcon className="h-4 w-4 shrink-0 rounded-sm" />
                    <span className="truncate">{f.archeName}</span>
                  </PickerRow>
                ))}
                <div className="px-2 py-1.5 text-[11px] text-muted-foreground">
                  Type to search all arches…
                </div>
              </>
            ) : (
              <div className="px-2 py-2 text-xs text-muted-foreground">
                Type to search all arches…
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
